import classNames from "classnames";
import Link from '@/components/AppLink';
import { useState } from "react";
import styles from "./ModelLibraryCard.module.scss";
import { Button } from "@/components";
import { axiosInstance } from "@/helpers/axios";
import { BRANDING_CARD_PLACEHOLDERS } from "../../../devices/_constants/branding-placeholders.const";
import {
  formatSpeciesDisplayName,
  getCatalogStatusBadgeMeta,
} from "../../_constants/content-models-ui.const";
import type { ModelsLibraryItem } from "../../_types/content-models.types";
import { getCatalogThumbnailSrc } from "../../_constants/models-thumbnails.const";

const PRIMARY_PREVIEW_SHADES = BRANDING_CARD_PLACEHOLDERS.map((b) => b.primary);

function primaryPreviewForSlug(slug: string) {
  let hash = 0;
  for (let i = 0; i < slug.length; i += 1) {
    hash = (hash + slug.charCodeAt(i) * (i + 1)) % 997;
  }
  return PRIMARY_PREVIEW_SHADES[hash % PRIMARY_PREVIEW_SHADES.length];
}

const PIPELINE = new Set(["draft", "in_review", "processing"]);

type ModelLibraryCardProps = {
  model: ModelsLibraryItem;
};

export const ModelLibraryCard = ({ model }: ModelLibraryCardProps) => {
  const [opening, setOpening] = useState(false);
  const showCornerBadge = PIPELINE.has(model.libraryDisplayStatus);
  const statusMeta = showCornerBadge
    ? getCatalogStatusBadgeMeta(model.libraryDisplayStatus)
    : null;

  const previewId = model.previewVersionId;
  const previewSt = model.previewVersionStatus;
  const showOpenPreview =
    Boolean(previewId) &&
    PIPELINE.has(previewSt ?? model.libraryDisplayStatus);

  const cover =
    model.coverImageUrl?.trim() ||
    getCatalogThumbnailSrc(model.slug) ||
    "";
  const mediaStyle =
    cover.length > 0
      ? undefined
      : { backgroundColor: primaryPreviewForSlug(model.slug) };

  const openPreview = async () => {
    if (!previewId) return;
    setOpening(true);
    try {
      const { data } = await axiosInstance.get(
        `/v2/models/versions/${encodeURIComponent(previewId)}/viewer-token`
      );
      const url = data?.data?.viewerUrl as string | undefined;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } finally {
      setOpening(false);
    }
  };

  return (
    <Link href={`/models/${model.slug}`} className={styles["comp"]}>
      <div className={styles["comp__media"]} style={mediaStyle} aria-hidden>
        {cover ? (
          <img src={cover} alt="" className={styles["comp__cover"]} />
        ) : null}
        {statusMeta ? (
          <span
            className={classNames(
              styles["comp__corner-badge"],
              styles[`comp__corner-badge--${statusMeta.variant}`]
            )}
          >
            {statusMeta.label}
          </span>
        ) : null}
        {showOpenPreview ? (
          <Button
            type="button"
            size="small"
            variant="secondary"
            className={styles["comp__preview-btn"]}
            disabled={opening}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void openPreview();
            }}
          >
            {opening ? "Opening…" : "Open Preview"}
          </Button>
        ) : null}
      </div>

      <div className={styles["comp__footer"]}>
        <span className={styles["comp__name"]}>
          {formatSpeciesDisplayName(model.displayName)}
        </span>
        <span className={styles["comp__platform"]} title="Live catalog version">
          {model.liveVersionLabel}
        </span>
      </div>
    </Link>
  );
};
